import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImageUrl } from '@/shared/hooks/useImageUrl';
import { getMultilingualValueForLang } from '@/shared/utils/languageUtils';
import { Work } from '@/shared/types';
import { ContentLanguageCode } from '@/shared/constants';
import pencilIcon from '@/assets/svg/pencil_icon.svg';
import checkIconCircle from '@/assets/svg/check_icon_circle.svg';
import draftIcon from '@/assets/svg/draft_icon.svg';
import headphoneIcon from '@/assets/svg/headphone_icon.svg';
import gpsIcon from '@/assets/svg/gps_icon.svg';
import walkIcon from '@/assets/svg/walk_icon.svg';

interface WorkCardProps {
    work: Work;
    currentLanguage: ContentLanguageCode;
    onEdit: (work: Work) => void;
    onDragStart: (workId: string) => void;
    onDragOver: (e: React.DragEvent, workId: string, place: 'before' | 'after') => void;
    onDragLeave: () => void;
    onDrop: (e: React.DragEvent, workId: string, place: 'before' | 'after') => void;
    onDragEnd: () => void;
    isDragging: boolean;
}

export const WorkCard: React.FC<WorkCardProps> = ({
    work,
    currentLanguage,
    onEdit,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
    isDragging,
}) => {
    const { t } = useTranslation();
    const imageUrl = useImageUrl(work.imageUrl);

    const formatLocation = () => {
        const parts: string[] = [];
        const floorLabel = getMultilingualValueForLang(work.floor, currentLanguage);
        const roomLabel = getMultilingualValueForLang(work.room, currentLanguage);
        if (floorLabel) {
            parts.push(floorLabel);
        }
        if (roomLabel) {
            parts.push(roomLabel);
        }
        return parts.join(' · ');
    };

    const formatDuration = () => {
        // Récupérer la durée de la langue courante (ou de la première langue disponible)
        const durationSeconds = work.durationTimeInSeconds?.[currentLanguage] || Object.values(work.durationTimeInSeconds || {})[0];

        if (durationSeconds) {
            const minutes = Math.ceil(durationSeconds / 60);
            return `${minutes}min`;
        }
        return '0min';
    };

    const getStatusBadge = () => {
        // Afficher le statut de la langue courante (ou draft par défaut)
        const currentStatus = work.status?.[currentLanguage] || 'draft';

        if (currentStatus === 'published') {
            return (
                <div className="work-status-badge published">
                    <img src={checkIconCircle} alt="" className="status-icon" />
                    <span>{t('exhibitions.published')}</span>
                </div>
            );
        }
        return (
            <div className="work-status-badge draft">
                <img src={draftIcon} alt="" className="status-icon" />
                <span>{t('exhibitions.draft')}</span>
            </div>
        );
    };

    const [isDraggingState, setIsDraggingState] = useState(false);

    const handleDragStart = (e: React.DragEvent) => {
        setIsDraggingState(true);
        onDragStart(work.id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', work.id);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        const place: 'before' | 'after' = e.clientY < mid ? 'before' : 'after';
        onDragOver(e, work.id, place);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingState(false);
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        const place: 'before' | 'after' = e.clientY < mid ? 'before' : 'after';
        onDrop(e, work.id, place);
    };

    const handleClick = (_e: React.MouseEvent) => {
        // Ne pas ouvrir l'édition si on vient de faire un drag
        if (isDraggingState) {
            setIsDraggingState(false);
            return;
        }
        onEdit(work);
    };

    return (
        <div
            className={`work-card ${isDragging ? 'dragging' : ''}`}
            onClick={handleClick}
            draggable
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={onDragLeave}
            onDrop={handleDrop}
            onMouseDown={(e) => {
                // Empêcher le drag si on clique sur le bouton d'édition
                if ((e.target as HTMLElement).closest('.work-card-edit-btn')) {
                    e.preventDefault();
                }
            }}
            onDragEnd={() => {
                setIsDraggingState(false);
                onDragEnd();
            }}
        >
            <div
                className="work-card-drag-handle"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="drag-dots">
                    <div className="drag-dots-container">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <div className="drag-dots-container">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
            <div className="work-card-thumbnail">
                {imageUrl ? (
                    <img src={imageUrl} alt={work.name[currentLanguage] || ''} />
                ) : (
                    <div className="work-card-placeholder">{t('common.noImage')}</div>
                )}
            </div>
            <div className="work-card-content">
                <h3 className="work-card-title">{work.name[currentLanguage] || t('exhibitions.noTitle')}</h3>
                <div className="work-card-metadata">
                    {work.pillNumber && work.pillNumber !== '-' && work.pillNumber !== '' && (
                        <div className="metadata-item">
                            <img src={headphoneIcon} alt="" className="metadata-icon" />
                            <span>{work.pillNumber}</span>
                        </div>
                    )}
                    {(getMultilingualValueForLang(work.floor, currentLanguage) || getMultilingualValueForLang(work.room, currentLanguage)) && (
                        <div className="metadata-item">
                            <img src={gpsIcon} alt="" className="metadata-icon" />
                            <span>{formatLocation()}</span>
                        </div>
                    )}
                    <div className="metadata-item">
                        <img src={walkIcon} alt="" className="metadata-icon" />
                        <span>{formatDuration()}</span>
                    </div>
                </div>
            </div>
            <div className="work-card-actions">
                {getStatusBadge()}
                <button
                    className="work-card-edit-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(work);
                    }}
                >
                    <img src={pencilIcon} alt="Éditer" />
                </button>
            </div>
        </div>
    );
};

